import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ControlRoomContext } from './ControlPage'
import BigModal from '../../../../components/Modals/BigModal'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import API from '../../../../services/API'
import {
  Close,
  LocationOn,
  Person,
  Phone,
  ReportProblem,
  Send,
} from '@mui/icons-material'
import { UserContext } from '../../../../App'
import IncidentLocationPicker from './IncidentLocationPicker'
import { calculateSuggestedPriority } from '../../../../helpers/callTriage'

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
  },
}

const ruleMatchesAnswer = (rule, answer) => {
  const operator = rule?.when?.operator
  const expected = rule?.when?.value

  if (operator === 'equals') return answer === expected
  if (operator === 'notEquals') return answer !== expected
  if (operator === 'exists')
    return answer !== undefined && answer !== null && answer !== ''
  if (operator === 'notExists')
    return answer === undefined || answer === null || answer === ''

  return false
}

const getAnswerTone = (question, answer) => {
  const matchingRules = (question.rules ?? []).filter((rule) =>
    ruleMatchesAnswer(rule, answer),
  )

  if (
    matchingRules.some((rule) =>
      ['setPriority', 'minimumPriority', 'increasePriority'].includes(
        rule.action?.type,
      ),
    )
  ) {
    return 'raise'
  }

  if (matchingRules.some((rule) => rule.action?.type === 'decreasePriority')) {
    return 'lower'
  }

  if (
    matchingRules.some((rule) =>
      ['showWarning', 'requireOverrideReason'].includes(rule.action?.type),
    )
  ) {
    return 'warning'
  }

  return 'neutral'
}

const selectedButtonSx = (tone) => {
  if (tone === 'raise') {
    return {
      backgroundColor: 'rgba(220,38,38,0.14)',
      color: '#991B1B',
      borderColor: 'rgba(220,38,38,0.5) !important',
    }
  }

  if (tone === 'lower') {
    return {
      backgroundColor: 'rgba(22,163,74,0.16)',
      color: '#166534',
      borderColor: 'rgba(22,163,74,0.55) !important',
    }
  }

  if (tone === 'warning') {
    return {
      backgroundColor: 'rgba(245,158,11,0.16)',
      color: '#92400E',
      borderColor: 'rgba(245,158,11,0.55) !important',
    }
  }

  return {
    backgroundColor: 'rgba(37,99,235,0.12)',
    color: '#1D4ED8',
    borderColor: 'rgba(37,99,235,0.35) !important',
  }
}

const normaliseSearch = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const complaintSearchText = (complaint) =>
  normaliseSearch(
    [
      complaint?.code,
      complaint?.category,
      complaint?.presentingComplaint,
      complaint?.description,
      ...(complaint?.keywords ?? []),
    ]
      .filter(Boolean)
      .join(' '),
  )

const filterTriageComplaints = (options, { inputValue }) => {
  const terms = normaliseSearch(inputValue).split(' ').filter(Boolean)
  if (!terms.length) return options

  return options.filter((option) => {
    const searchText = complaintSearchText(option)
    return terms.every((term) => searchText.includes(term))
  })
}

const QuickAnswerQuestion = ({ question, value, onChange }) => {
  const questionId = String(question._id)
  const isBinary = question.type === 'yesNo'
  const selectedTone = getAnswerTone(question, value)

  if (!isBinary) {
    return (
      <TextField
        label={question.text}
        helperText={question.helpText}
        required={question.required}
        fullWidth
        select={['singleSelect'].includes(question.type)}
        type={question.type === 'number' ? 'number' : 'text'}
        value={value ?? ''}
        onChange={(e) =>
          onChange(
            questionId,
            question.type === 'number'
              ? Number(e.target.value)
              : e.target.value,
          )
        }
      >
        {(question.options ?? []).map((option) => (
          <MenuItem key={option.label} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2,
        borderColor: value ? 'rgba(35,154,164,0.35)' : 'rgba(15,23,42,0.12)',
        backgroundColor: value ? 'rgba(35,154,164,0.04)' : 'white',
      }}
    >
      <Stack spacing={1}>
        <Typography fontWeight={800} color="#242F40">
          {question.text}
        </Typography>
        {question.helpText && (
          <Typography variant="caption" color="text.secondary">
            {question.helpText}
          </Typography>
        )}
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={value ?? null}
          onChange={(_, nextValue) => {
            onChange(questionId, nextValue ?? undefined)
          }}
          sx={{
            gap: 1,
            '& .MuiToggleButtonGroup-grouped': {
              border: '1px solid rgba(15,23,42,0.14) !important',
              borderRadius: '12px !important',
              minHeight: 48,
              fontWeight: 900,
              textTransform: 'none',
            },
          }}
        >
          <ToggleButton
            value="Yes"
            sx={{
              '&.Mui-selected': {
                ...selectedButtonSx(selectedTone),
              },
              '&.Mui-selected:hover': {
                ...selectedButtonSx(selectedTone),
              },
            }}
          >
            Yes
          </ToggleButton>
          <ToggleButton
            value="No"
            sx={{
              '&.Mui-selected': {
                ...selectedButtonSx(selectedTone),
              },
              '&.Mui-selected:hover': {
                ...selectedButtonSx(selectedTone),
              },
            }}
          >
            No
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Paper>
  )
}

const NewIncident = () => {
  const {
    openNewIncident,
    setOpenNewIncident,
    selectedControlRoom,
    eventMap,
    mapView,
    refresh,
  } = useContext(ControlRoomContext)
  const { user } = useContext(UserContext)

  const creatingDraftRef = useRef(false)
  const draftAutosaveTimerRef = useRef(null)
  const [draftIncident, setDraftIncident] = useState(null)
  const [incidentType, setIncidentType] = useState('Medical')
  const [presentingComplaint, setPresentingComplaint] = useState('')
  const [priority, setPriority] = useState('Moderate')
  const [callTriageComplaints, setCallTriageComplaints] = useState([])
  const [callTriageLoading, setCallTriageLoading] = useState(false)
  const [selectedTriageComplaint, setSelectedTriageComplaint] = useState(null)
  const [triageAnswers, setTriageAnswers] = useState({})
  const [finalPriority, setFinalPriority] = useState('Moderate')
  const [overrideReason, setOverrideReason] = useState('')
  const [location, setLocation] = useState('')
  const [what3words, setWhat3words] = useState('')
  const [coordinates, setCoordinates] = useState('')
  const [eventMapCoordinates, setEventMapCoordinates] = useState(null)
  const [callerName, setCallerName] = useState('')
  const [callerPhone, setCallerPhone] = useState('')
  const [callerRole, setCallerRole] = useState('Event Control')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [draftError, setDraftError] = useState(false)

  const isCallTakerOnly = user?.employee?.cadAccess === 'Call Taker'

  const resetForm = () => {
    setDraftIncident(null)
    setIncidentType('Medical')
    setPresentingComplaint('')
    setPriority('Moderate')
    setSelectedTriageComplaint(null)
    setTriageAnswers({})
    setFinalPriority('Moderate')
    setOverrideReason('')
    setLocation('')
    setWhat3WordsSafe('')
    setCoordinates('')
    setEventMapCoordinates(null)
    setCallerName('')
    setCallerPhone('')
    setCallerRole('Event Control')
    setDescription('')
    setDraftError(false)
  }

  const setWhat3WordsSafe = (value) => setWhat3words(value)

  useEffect(() => {
    if (!openNewIncident) return
    setCallTriageLoading(true)
    API.get('/configuration/command-control/call-triage/approved')
      .then((res) => {
        if (res.status === 200) setCallTriageComplaints(res.data ?? [])
      })
      .finally(() => setCallTriageLoading(false))
  }, [openNewIncident])

  const triagePreview = useMemo(() => {
    if (!selectedTriageComplaint) return null
    return calculateSuggestedPriority({
      defaultPriority: selectedTriageComplaint.defaultPriority,
      questions: selectedTriageComplaint.questions,
      answers: triageAnswers,
    })
  }, [selectedTriageComplaint, triageAnswers])

  const triageQuestionCount = selectedTriageComplaint?.questions?.length ?? 0
  const answeredTriageQuestionCount = useMemo(
    () =>
      (selectedTriageComplaint?.questions ?? []).filter((question) => {
        const answer = triageAnswers[String(question._id)]
        return answer !== undefined && answer !== null && answer !== ''
      }).length,
    [selectedTriageComplaint, triageAnswers],
  )
  const hasIncidentInput = useMemo(() => {
    const hasTriageAnswer = Object.values(triageAnswers ?? {}).some(
      (answer) => answer !== undefined && answer !== null && answer !== '',
    )

    return [
      selectedTriageComplaint?.presentingComplaint,
      presentingComplaint,
      location,
      what3words,
      coordinates,
      description,
      callerName,
      callerPhone,
      overrideReason,
    ].some((value) => String(value ?? '').trim()) ||
      hasTriageAnswer ||
      (Array.isArray(eventMapCoordinates) && eventMapCoordinates.length === 2)
  }, [
    selectedTriageComplaint,
    presentingComplaint,
    location,
    what3words,
    coordinates,
    description,
    callerName,
    callerPhone,
    overrideReason,
    triageAnswers,
    eventMapCoordinates,
  ])

  useEffect(() => {
    if (!selectedTriageComplaint) return
    setPriority(selectedTriageComplaint.defaultPriority)
    setFinalPriority(selectedTriageComplaint.defaultPriority)
  }, [selectedTriageComplaint])

  useEffect(() => {
    if (!triagePreview) return
    setPriority(triagePreview.calculatedPriority)
    setFinalPriority(triagePreview.calculatedPriority)
  }, [triagePreview?.calculatedPriority])

  useEffect(() => {
    if (!openNewIncident || !draftIncident?.id || !selectedControlRoom) {
      return undefined
    }

    if (draftAutosaveTimerRef.current) {
      clearTimeout(draftAutosaveTimerRef.current)
    }

    draftAutosaveTimerRef.current = setTimeout(() => {
      const calculatedSuggestedPriority = triagePreview?.calculatedPriority
      const requestedFinalPriority =
        finalPriority || calculatedSuggestedPriority || priority
      const hasUnresolvedOverride =
        selectedTriageComplaint &&
        calculatedSuggestedPriority &&
        requestedFinalPriority !== calculatedSuggestedPriority &&
        !overrideReason?.trim()

      const payload = {
        incidentID: draftIncident.id,
        controlRoom: selectedControlRoom,
        incidentType,
        presentingComplaint:
          selectedTriageComplaint?.presentingComplaint || presentingComplaint,
        location,
        what3words,
        coordinates,
        eventMapCoordinates,
        priority: selectedTriageComplaint
          ? hasUnresolvedOverride
            ? calculatedSuggestedPriority
            : requestedFinalPriority
          : priority,
        description,
        caller: {
          name: callerName,
          phone: callerPhone,
          role: callerRole,
        },
        logNote: 'Draft incident details updated',
        suppressTimeline: true,
      }

      if (selectedTriageComplaint && !hasUnresolvedOverride) {
        payload.callTriage = {
          complaintId: selectedTriageComplaint._id,
          answers: triageAnswers,
          finalPriority: requestedFinalPriority,
          overrideReason,
        }
      }

      API.post('/commandcontrol/incidentupdate', payload).then((res) => {
        if (res.status === 200) refresh?.()
      })
    }, 600)

    return () => {
      if (draftAutosaveTimerRef.current) {
        clearTimeout(draftAutosaveTimerRef.current)
      }
    }
  }, [
    openNewIncident,
    draftIncident?.id,
    selectedControlRoom,
    selectedTriageComplaint,
    triageAnswers,
    finalPriority,
    overrideReason,
    triagePreview?.calculatedPriority,
    incidentType,
    presentingComplaint,
    location,
    what3words,
    coordinates,
    eventMapCoordinates,
    priority,
    description,
    callerName,
    callerPhone,
    callerRole,
    refresh,
  ])

  useEffect(() => {
    if (!openNewIncident || !selectedControlRoom || draftIncident) return
    if (creatingDraftRef.current) return

    creatingDraftRef.current = true
    setDraftError(false)

    API.post('/commandcontrol/newincident', {
      controlRoom: selectedControlRoom,
      incidentStatus: 'New',
      incidentType,
      priority,
      caller: {
        role: callerRole,
      },
      description: '',
      location: '',
    })
      .then((res) => {
        if (res.status === 200) {
          setDraftIncident(res.data)
          refresh?.()
        }
      })
      .catch(() => setDraftError(true))
      .finally(() => {
        creatingDraftRef.current = false
      })
  }, [openNewIncident, selectedControlRoom, draftIncident])

  const handleCancel = () => {
    setOpenNewIncident(false)
    resetForm()
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (submitting || !draftIncident?.id) return
    if (callTriageLoading) return
    if (callTriageComplaints.length && !selectedTriageComplaint) return
    if (!hasIncidentInput) return

    setSubmitting(true)

    API.post('/commandcontrol/incidentupdate', {
      incidentID: draftIncident.id,
      controlRoom: selectedControlRoom,
      incidentType,
      presentingComplaint:
        selectedTriageComplaint?.presentingComplaint || presentingComplaint,
      location,
      what3words,
      coordinates,
      eventMapCoordinates,
      priority,
      callTriage: selectedTriageComplaint
        ? {
            complaintId: selectedTriageComplaint._id,
            answers: triageAnswers,
            finalPriority,
            overrideReason,
          }
        : undefined,
      description,
      caller: {
        name: callerName,
        phone: callerPhone,
        role: callerRole,
      },
      logNote: 'Initial incident details recorded',
    })
      .then((res) => {
        if (res.status === 200) {
          refresh?.()
          handleCancel()
        }
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <BigModal
      open={openNewIncident}
      onClose={() => {}}
      style={{
        width: { xs: '96vw', md: '80%' },
        maxWidth: { xs: '96vw', md: '90vw' },
        maxHeight: { xs: '92vh', md: '85vh' },
      }}
    >
      <Box>
        <Box
          sx={{
            mx: -3,
            mt: -3,
            mb: 1.5,
            p: { xs: 3, md: 6 },
            color: 'white',
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 55%, #E32525 155%)',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
              }}
            >
              <ReportProblem sx={{ fontSize: 30 }} />
            </Box>

            <Stack flex={1}>
              <Typography variant="overline" sx={{ opacity: 0.72 }}>
                Command & Control
              </Typography>

              <Typography variant="h5" fontWeight={900} color="white">
                {isCallTakerOnly ? 'Call Taker Script' : 'Create New Incident'}
              </Typography>

              <Typography variant="body2" sx={{ color: '#fff', opacity: 0.78 }}>
                Record the incident details and initial caller information.
              </Typography>
              {draftError && (
                <Alert severity="error" sx={{ borderRadius: 3 }}>
                  Unable to start the incident. Please close this window and try
                  again.
                </Alert>
              )}
            </Stack>

            <IconButton
              aria-label="Cancel new incident"
              onClick={handleCancel}
              disabled={submitting}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.10)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
              }}
            >
              <Close />
            </IconButton>
          </Stack>
        </Box>

        <Stack
          component="form"
          onSubmit={handleSubmit}
          spacing={2.5}
          padding={2}
        >
          {isCallTakerOnly && (
            <Alert
              severity="info"
              sx={{
                borderRadius: 3,
                backgroundColor: '#E6F7F9',
                color: '#1E7F88',
                border: '1px solid rgba(35,154,164,0.24)',
              }}
            >
              Follow the scripted triage questions. Dispatch will confirm the
              final incident type and priority.
            </Alert>
          )}

          {!hasIncidentInput && (
            <Alert severity="warning" variant="outlined">
              Add at least one incident detail before saving, or cancel this
              draft.
            </Alert>
          )}

          <Stack spacing={1}>
            <Typography fontWeight={900} color="#242F40">
              Incident Details
            </Typography>

            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={3}>
                  <FormControl fullWidth required sx={fieldSx}>
                    <InputLabel>Incident Type</InputLabel>
                    <Select
                      value={incidentType}
                      onChange={(e) => setIncidentType(e.target.value)}
                      label="Incident Type"
                    >
                      {[
                        'Medical',
                        'Transfer',
                        'Patient Transport',
                        'Fire',
                        'Security',
                        'Crowd',
                        'Logistical',
                        'Other',
                      ].map((x) => (
                        <MenuItem key={x} value={x}>
                          {x}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {!selectedTriageComplaint && !callTriageComplaints.length && (
                  <Grid item xs={12} lg={3}>
                    <FormControl fullWidth required sx={fieldSx}>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                        label="Priority"
                      >
                        {['Critical', 'High', 'Moderate', 'Low'].map((x) => (
                          <MenuItem key={x} value={x}>
                            {x}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} lg={6}>
                  <TextField
                    label="Location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    required
                    autoComplete="off"
                    fullWidth
                    sx={fieldSx}
                    placeholder='Text location i.e. "Main Gate"'
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="What3Words"
                    fullWidth
                    autoComplete="off"
                    value={what3words}
                    onChange={(e) => setWhat3words(e.target.value)}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={
                      isCallTakerOnly
                        ? 'Incident summary / script notes'
                        : 'Summary'
                    }
                    fullWidth
                    autoComplete="off"
                    multiline
                    rows={isCallTakerOnly ? 3 : 2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={fieldSx}
                  />
                </Grid>

                {!callTriageLoading && !callTriageComplaints.length && (
                  <Grid item xs={12}>
                    <TextField
                      label="Presenting Complaint"
                      value={presentingComplaint}
                      onChange={(e) => setPresentingComplaint(e.target.value)}
                      autoComplete="off"
                      fullWidth
                      sx={fieldSx}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              borderColor: 'rgba(35,154,164,0.28)',
            }}
          >
            <Stack spacing={2}>
              <Typography fontWeight={900} color="#242F40">
                Call Triage
              </Typography>

              {callTriageLoading && <LinearProgress />}

              {!callTriageLoading && !callTriageComplaints.length && (
                <Alert severity="warning" variant="outlined">
                  No approved and enabled call triage complaints are available.
                  Create and approve an organisation-approved triage
                  configuration before call handlers use this workflow.
                </Alert>
              )}

              {!!callTriageComplaints.length && (
                <Autocomplete
                  options={callTriageComplaints}
                  filterOptions={filterTriageComplaints}
                  value={selectedTriageComplaint}
                  onChange={(_, value) => {
                    setSelectedTriageComplaint(value)
                    setTriageAnswers({})
                    setOverrideReason('')
                  }}
                  groupBy={(option) => option.category}
                  getOptionLabel={(option) =>
                    option
                      ? `${option.presentingComplaint} (${option.defaultPriority})`
                      : ''
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Presenting complaint"
                      helperText="Only approved and enabled call triage complaints are shown."
                    />
                  )}
                />
              )}

              {selectedTriageComplaint && (
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    gap={1}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Rapid triage questions
                    </Typography>
                    <Chip
                      size="small"
                      label={`${answeredTriageQuestionCount}/${triageQuestionCount} answered`}
                      color={
                        answeredTriageQuestionCount === triageQuestionCount
                          ? 'success'
                          : 'default'
                      }
                    />
                  </Stack>
                  <Grid container spacing={2}>
                    {(selectedTriageComplaint.questions ?? [])
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                      .map((question) => (
                        <Grid item xs={12} md={6} key={question._id}>
                          <QuickAnswerQuestion
                            question={question}
                            value={triageAnswers[String(question._id)] ?? ''}
                            onChange={(questionId, answer) =>
                              setTriageAnswers((prev) => ({
                                ...prev,
                                [questionId]: answer,
                              }))
                            }
                          />
                        </Grid>
                      ))}
                  </Grid>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    <Chip
                      label={`Suggested Priority: ${
                        triagePreview?.calculatedPriority ??
                        selectedTriageComplaint.defaultPriority
                      }`}
                      color="primary"
                    />
                    <Chip
                      label={`Default Suggested Priority: ${selectedTriageComplaint.defaultPriority}`}
                    />
                  </Stack>
                  {!!triagePreview?.warnings?.length && (
                    <Alert severity="warning">
                      {triagePreview.warnings.join(' ')}
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth required sx={fieldSx}>
                        <InputLabel>Final Priority</InputLabel>
                        <Select
                          value={finalPriority}
                          onChange={(e) => setFinalPriority(e.target.value)}
                          label="Final Priority"
                        >
                          {['Critical', 'High', 'Moderate', 'Low'].map((x) => (
                            <MenuItem key={x} value={x}>
                              {x}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {(finalPriority !== triagePreview?.calculatedPriority ||
                      triagePreview?.overrideReasonRequired) && (
                      <Grid item xs={12} md={8}>
                        <TextField
                          label="Override Reason"
                          value={overrideReason}
                          onChange={(e) => setOverrideReason(e.target.value)}
                          required
                          fullWidth
                          sx={fieldSx}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Stack>
              )}
            </Stack>
          </Paper>

          <Grid item xs={12}>
            <IncidentLocationPicker
              coordinates={coordinates}
              setCoordinates={setCoordinates}
              eventMapCoordinates={eventMapCoordinates}
              setEventMapCoordinates={setEventMapCoordinates}
              eventMap={eventMap}
              initialMapView={mapView}
            />
          </Grid>

          <Divider />

          <Stack spacing={1}>
            <Typography fontWeight={900} color="#242F40">
              Caller Information
            </Typography>
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Caller name"
                    fullWidth
                    value={callerName}
                    onChange={(e) => setCallerName(e.target.value)}
                    sx={fieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Phone number"
                    fullWidth
                    value={callerPhone}
                    onChange={(e) => setCallerPhone(e.target.value)}
                    sx={fieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    freeSolo
                    onChange={(e, newValue) => setCallerRole(newValue)}
                    value={callerRole}
                    options={[
                      'First Party Caller',
                      'Third Party Caller',
                      'Police',
                      'Fire',
                      'NHS Ambulance Service',
                      'Running Call',
                      'Self Presenting',
                      'Event Control',
                      'Steward/Security',
                      'Medical Team',
                      'Venue Staff',
                      'Public',
                    ]}
                    renderInput={(props) => (
                      <TextField
                        label="Caller Role"
                        {...props}
                        helperText="Type a custom value and press 'Enter'"
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>

          <Divider />

          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5}>
            <Button
              fullWidth
              onClick={handleCancel}
              disabled={submitting}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 800,
                color: '#64748B',
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={
                !draftIncident?.id ||
                submitting ||
                callTriageLoading ||
                (callTriageComplaints.length && !selectedTriageComplaint) ||
                !hasIncidentInput
              }
              endIcon={
                submitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Send />
                )
              }
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 900,
                backgroundColor: isCallTakerOnly ? '#239AA4' : '#E32525',
                boxShadow: isCallTakerOnly
                  ? '0 12px 28px rgba(35,154,164,0.28)'
                  : '0 12px 28px rgba(227,37,37,0.24)',
                '&:hover': {
                  backgroundColor: isCallTakerOnly ? '#1E7F88' : '#B91C1C',
                },
              }}
            >
              {isCallTakerOnly ? 'Save Script & Close' : 'Save Details & Close'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </BigModal>
  )
}

export default NewIncident
